import { type CSSProperties, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { chipRecipe, chipIcon, chipDeleteButton, chipLabelTruncate, chipClickableTruncate } from './chip.css';
import { tagText } from '../TagText/tagText.css';
import { wrapTextChild } from '../wrapTextChild';

export type ChipSize    = 'small' | 'medium';
export type ChipVariant = 'filled' | 'outlined' | 'soft';
export type ChipIntent  = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

export interface ChipProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick' | 'color'> {
  label: ReactNode;
  size?:    ChipSize;
  variant?: ChipVariant;
  intent?:  ChipIntent;
  icon?:    ReactNode;
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  onClick?:  (event: MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  deleteButtonTestId?: string;
  /** When true, wraps the label in the tagText class (uppercase + reduced size). */
  tag?: boolean;
  /** When true, the label ends in "…" if the chip is width-limited (e.g. `maxWidth`), and the
   *  delete button stays inside the chip. Opt-in: without it the DOM is unchanged. */
  truncateLabel?: boolean;
}

export function Chip({
  label,
  size    = 'medium',
  variant = 'filled',
  intent  = 'default',
  icon,
  onDelete,
  onClick,
  disabled,
  className,
  style,
  deleteButtonTestId,
  tag = false,
  truncateLabel = false,
  ...rest
}: ChipProps) {
  const isClickable = Boolean(onClick) && !disabled;
  const isDeletable = Boolean(onDelete) && !disabled;
  const tagged = tag ? <span className={tagText}>{label}</span> : label;
  const renderedLabel = truncateLabel ? (
    <span className={chipLabelTruncate}>{wrapTextChild(tagged)}</span>
  ) : tagged;

  // F12: when role="button" + tabIndex={0}, ARIA contract requires the
  // element to be activatable from the keyboard. The native onClick handler
  // doesn't fire on Enter/Space for a <div>, so wire it explicitly.
  const handleKeyDown = isClickable && onClick
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as MouseEvent<HTMLDivElement>);
        }
      }
    : undefined;

  const chipClassName = clsx(
    chipRecipe({
      size,
      variant,
      intent,
      clickable: isClickable,
      deletable: isDeletable,
    }),
    className,
  );

  const deleteButton = isDeletable ? (
    <button
      type="button"
      className={chipDeleteButton}
      onClick={(e) => {
        e.stopPropagation();
        onDelete?.(e);
      }}
      aria-label="Remove"
      data-testid={deleteButtonTestId}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 16 16"
        width="12"
        height="12"
        style={{ display: 'block' }}
      >
        <path
          d="M4 4l8 8M12 4l-8 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  ) : null;

  // F12 (secondary): when the chip is BOTH clickable and deletable, we must
  // not nest the delete <button> inside the role="button" parent (invalid
  // ARIA: interactive-in-interactive). Split the clickable content into its
  // own role=button element and render the delete button as a sibling inside
  // a non-interactive wrapper.
  if (isClickable && isDeletable) {
    return (
      <div className={chipClassName} style={style} {...rest}>
        <span
          role="button"
          tabIndex={0}
          onClick={!disabled ? (onClick as unknown as React.MouseEventHandler<HTMLSpanElement>) : undefined}
          onKeyDown={handleKeyDown as unknown as React.KeyboardEventHandler<HTMLSpanElement>}
          className={truncateLabel ? chipClickableTruncate : undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {icon && <span className={chipIcon}>{icon}</span>}
          {wrapTextChild(renderedLabel)}
        </span>
        {deleteButton}
      </div>
    );
  }

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      className={chipClassName}
      style={style}
      onClick={!disabled ? onClick : undefined}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {icon && <span className={chipIcon}>{icon}</span>}
      {wrapTextChild(renderedLabel)}
      {deleteButton}
    </div>
  );
}
